import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Study, CreateStudyDto } from '../services/studies';

interface StudyFormModalProps {
  study?: Study | null;
  onSubmit: (data: any) => Promise<void>;
  onClose: () => void;
  subjects?: string[];
  categories?: string[];
  themes?: string[];
  types?: string[];
  platforms?: string[];
}

const StudyFormModal: React.FC<StudyFormModalProps> = ({
  study,
  onSubmit,
  onClose,
  subjects = [],
}) => {
  const [formData, setFormData] = useState<CreateStudyDto>({
    title: '',
    description: '',
    subject: '',
    start: '',
    end: '',
    category: '',
    theme: '',
    what: '',
    where: '',
    link: undefined,
    status: 'not_started',
    priority: 'medium',
    tags: [],
    estimatedDuration: undefined,
    notes: '',
    dueDate: '',
    progressPercentage: 0,
    reminderEnabled: false,
    reminderFrequency: 7,
    reminderUnit: 'days',
  });
  const [loading, setLoading] = useState(false);
  const [showSubjectSuggestions, setShowSubjectSuggestions] = useState(false);

  useEffect(() => {
    if (study) {
      setFormData({
        title: study.title,
        description: study.description || '',
        subject: study.subject,
        start: study.start || '',
        end: study.end || '',
        category: study.category,
        theme: study.theme,
        what: study.what,
        where: study.where,
        link: study.link || '',
        status: study.status,
        priority: study.priority,
        tags: study.tags || [],
        estimatedDuration: study.estimatedDuration,
        notes: study.notes || '',
        dueDate: study.dueDate ? new Date(study.dueDate).toISOString().split('T')[0] : '',
        progressPercentage: study.progressPercentage,
        reminderEnabled: study.reminderEnabled || false,
        reminderFrequency: study.reminderFrequency || 7,
        reminderUnit: study.reminderUnit || 'days',
      });
    } else {
      setFormData({
        title: '',
        description: '',
        subject: '',
        start: '',
        end: '',
        category: '',
        theme: '',
        what: '',
        where: '',
        link: undefined,
        status: 'not_started',
        priority: 'medium',
        tags: [],
        estimatedDuration: undefined,
        notes: '',
        dueDate: '',
        progressPercentage: 0,
        reminderEnabled: false,
        reminderFrequency: 7,
        reminderUnit: 'days',
      });
    }
  }, [study]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cleanedData: any = {
        ...formData,
        start: formData.start || null,
        end: formData.end || null,
        dueDate: formData.dueDate || null,
        estimatedDuration: formData.estimatedDuration || undefined,
        description: formData.description || undefined,
        notes: formData.notes || undefined,
      };

      // Handle link field: send trimmed value, null if empty (to clear it), or don't include if creating new study
      const trimmedLink = formData.link?.trim();
      if (trimmedLink) {
        cleanedData.link = trimmedLink;
      } else if (study) {
        // When editing, explicitly set to null to clear the field
        cleanedData.link = null;
      }
      // When creating new study without link, don't include the field at all

      await onSubmit(cleanedData);
      onClose();
    } catch (error) {
      console.error('Erro ao enviar estudo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateStudyDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const filteredSubjectSuggestions = formData.subject
    ? subjects.filter(item => item.toLowerCase().includes((formData.subject as string).toLowerCase()))
    : subjects;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center text-center sm:items-center sm:p-4">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75 dark:opacity-90" onClick={onClose}></div>
        </div>

        <div className="relative w-full bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:max-w-2xl flex flex-col max-h-[92vh]">
          <div className="flex items-center justify-between px-4 py-4 sm:px-6 border-b border-gray-200 dark:border-gray-700 shrink-0">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              {study ? 'Editar Estudo' : 'Novo Estudo'}
            </h3>
            <button
              onClick={onClose}
              className="p-1 -mr-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="px-4 sm:px-6 py-4 space-y-4 overflow-y-auto flex-1">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Assunto
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => {
                    handleInputChange('subject', e.target.value);
                    setShowSubjectSuggestions(true);
                  }}
                  onFocus={() => setShowSubjectSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSubjectSuggestions(false), 200)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
                  placeholder="Ex: Tecnologia"
                />
                {showSubjectSuggestions && formData.subject && filteredSubjectSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-40 overflow-y-auto">
                    {filteredSubjectSuggestions.map((item, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          handleInputChange('subject', item);
                          setShowSubjectSuggestions(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm"
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
                  placeholder="Descrição do estudo"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800 disabled:opacity-50"
              >
                {loading ? 'Salvando...' : (study ? 'Atualizar' : 'Criar')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StudyFormModal;
